import { useFulfillmentPlannerContext } from '../../context';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { NumberFormattingService } from '../../../services/number-formatting-service';
import type { Currency } from '../../../prop-types/currency-types';
import type { AlternativeRatesRecommendation } from '../types/recommendation-types';
import { useRateShopperRecommendationTracking } from './use-rate-shopper-recommendation-tracking';

const emptyCurrency: Currency = { value: 0, code: '' };

export const useRateShopperRecommendation = (
  fulfillmentPlanId: string,
  setShouldShowRateShopperRecommendation: Dispatch<SetStateAction<boolean>>,
  alternativeRatesRecommendation: AlternativeRatesRecommendation | undefined,
) => {
  const { onServiceChangeWithSideEffects } = useFulfillmentPlannerContext();
  const { trackRecommendationShown, trackRecommendationApplied, resetTracking } = useRateShopperRecommendationTracking(fulfillmentPlanId);

  const applicableRecommendation = useMemo(() => {
    const userTotal = alternativeRatesRecommendation?.userRatesTotalCost?.value;
    const rateShopperRecommendation = alternativeRatesRecommendation?.rateShopper;
    const recommendationTotal = rateShopperRecommendation?.totalCost?.value;

    const userRatesTotalCost = alternativeRatesRecommendation?.userRatesTotalCost;

    if (rateShopperRecommendation && userTotal && recommendationTotal && userTotal > recommendationTotal && userRatesTotalCost) {
      return {
        recommendation: rateShopperRecommendation,
        savings: {
          value: NumberFormattingService.roundToPlace(userTotal - recommendationTotal, 2) || 0,
          code: userRatesTotalCost.code ?? '',
        } as Currency,
        userRatesTotalCost,
      };
    }

    return undefined;
  }, [alternativeRatesRecommendation?.rateShopper, alternativeRatesRecommendation?.userRatesTotalCost]);

  useEffect(() => {
    if (applicableRecommendation) {
      const { recommendation, savings, userRatesTotalCost } = applicableRecommendation;
      setShouldShowRateShopperRecommendation(true);
      trackRecommendationShown({
        amountCheaperValue: savings.value,
        amountCheaperCode: savings.code,
        serviceCollectionGuid: recommendation.serviceCollectionGuid,
        serviceId: recommendation.serviceId,
        sellerLabelProviderId: recommendation.sellerProviderId,
        carrierId: recommendation.carrierId,
        insuranceProviderId: recommendation.insuranceProviderId,
        rateShopperTotalCost: recommendation.totalCost,
        userRatesTotalCost,
      });
    } else {
      setShouldShowRateShopperRecommendation(false);
      resetTracking();
    }
  }, [applicableRecommendation, setShouldShowRateShopperRecommendation, trackRecommendationShown, resetTracking]);

  const applyRateShopperRecommendation = useCallback(() => {
    if (applicableRecommendation?.recommendation?.serviceCollectionGuid && onServiceChangeWithSideEffects) {
      onServiceChangeWithSideEffects({
        serviceCollectionGuid: applicableRecommendation.recommendation.serviceCollectionGuid,
        sendAnalytics: trackRecommendationApplied,
      });
    }
  }, [onServiceChangeWithSideEffects, applicableRecommendation?.recommendation?.serviceCollectionGuid, trackRecommendationApplied]);

  return {
    applyRateShopperRecommendation,
    rateShopperRecommendationSavings: applicableRecommendation?.savings ?? emptyCurrency,
  };
};
